// Durable mutation queue + conflict list (localforage) drained when back online.
import localforage from "localforage-esm";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { fieldsFromSerializedBody, extractProjectIdFromUrl } from "./bodySerialize";

const forage = localforage.createInstance({
  name: "LionOffline",
  storeName: "outbox",
});

const QUEUE_KEY = "queue";
const CONFLICTS_KEY = "conflicts";

export const getOutbox = async () => {
  return (await forage.getItem(QUEUE_KEY)) || [];
};

export const setOutbox = async (queue) => {
  await forage.setItem(QUEUE_KEY, queue);
  return queue;
};

export const enqueueMutation = async (item) => {
  const entry = {
    id: uuidv4(),
    createdAt: Date.now(),
    status: "pending", // pending | conflict | failed
    ...item,
  };
  const queue = await getOutbox();
  queue.push(entry);
  await setOutbox(queue);
  return entry;
};

export const updateOutboxItem = async (id, patch) => {
  const queue = await getOutbox();
  const next = _.map(queue, (item) => (item.id === id ? { ...item, ...patch } : item));
  await setOutbox(next);
  return _.find(next, { id });
};

export const removeOutboxItem = async (id) => {
  const queue = await getOutbox();
  await setOutbox(_.reject(queue, { id }));
};

export const pendingCount = async () => {
  const queue = await getOutbox();
  return _.filter(queue, (i) => i.status === "pending" || i.status === "failed").length;
};

const SLICE_LABELS = {
  project_qa_action_items: "Action Item",
  project_qa_paint_inspections: "Paint Inspection",
  project_qa_welding_inspections: "Welding Inspection",
  project_qa_site_site_installations: "Site Installation",
  project_qa_site_surveillances: "Surveillance",
  custom_form_records: "Form Record",
  store_bookout_items: "Bookout",
  store_overalls: "Overalls",
  store_requests: "Store Request",
};

const fieldFromOutbox = (item, name) => {
  const entry = _.find(item.entries, (e) => e.kind === "string" && _.endsWith(e.key, `[${name}]`));
  if (entry) return entry.value;
  const nested = item.bodyJson && _.find(_.values(item.bodyJson), (v) => _.isPlainObject(v) && v[name] != null);
  return nested?.[name] || item.bodyJson?.[name];
};

const SKIP_PREVIEW_KEYS = new Set([
  "id",
  "project_id",
  "current_project_id",
  "created_at",
  "updated_at",
  "authenticity_token",
  "blob_signed_id",
  "localPath",
  "pendingUpload",
  "_pendingSync",
]);

const PREVIEW_LABELS = {
  description: "Description",
  name: "Name",
  display_name: "Name",
  comments: "Comments",
  notes: "Notes",
  location: "Location",
  assembly_display_name_full: "Assembly",
  assembly_display_name: "Assembly",
  mark: "Mark",
  area: "Area",
  status: "Status",
  date: "Date",
  inspected_date: "Inspected",
};

const photoCountFromItem = (item) => {
  const files = _.filter(item.entries, (e) => e.kind === "file" || e.attachmentUpload || e.localPath);
  const localPaths = _.filter(item.entries, (e) => e.kind === "string" && _.endsWith(e.key, "[localPath]") && e.value);
  return files.length + localPaths.length;
};

export const outboxItemPreview = (item) => {
  const fields = fieldsFromSerializedBody(item);
  const kind = SLICE_LABELS[item.entitySlice] || (_.includes(item.url, "/current_project") ? "Project" : "Record");
  const verb = _.includes(item.url, "/current_project")
    ? "Switch"
    : item.method === "post"
      ? "Create"
      : item.method === "delete"
        ? "Delete"
        : "Update";

  const title =
    fieldFromOutbox(item, "description") ||
    fieldFromOutbox(item, "name") ||
    fieldFromOutbox(item, "display_name") ||
    fieldFromOutbox(item, "assembly_display_name_full") ||
    fieldFromOutbox(item, "assembly_display_name") ||
    (fields.current_project_id ? `Project ${fields.current_project_id}` : null);

  const highlights = [];
  const seen = new Set();
  _.each(PREVIEW_LABELS, (label, key) => {
    const value = fields[key] || fieldFromOutbox(item, key);
    if (value == null || value === "" || SKIP_PREVIEW_KEYS.has(key)) return;
    if (String(value) === String(title) && highlights.length === 0) return;
    if (seen.has(label)) return;
    seen.add(label);
    highlights.push({ label, value: String(value) });
  });

  return {
    verb,
    kind,
    title: title ? String(title) : null,
    projectId: extractProjectIdFromUrl(item.url) || fields.project_id || fields.current_project_id || null,
    photoCount: photoCountFromItem(item),
    highlights: _.take(highlights, 4),
  };
};

export const outboxItemLabel = (item) => {
  const preview = outboxItemPreview(item);
  return preview.title ? `${preview.verb} ${preview.kind}: ${preview.title}` : `${preview.verb} ${preview.kind}`;
};

export const slimOutboxForUi = (queue) =>
  _.map(
    _.filter(queue, (i) => i.status === "pending" || i.status === "failed"),
    (i) => {
      const preview = outboxItemPreview(i);
      return {
        id: i.id,
        method: i.method,
        url: i.url,
        status: i.status,
        entitySlice: i.entitySlice,
        entityId: i.entityId,
        label: outboxItemLabel(i),
        createdAt: i.createdAt,
        lastError: i.lastError || null,
        attempts: i.attempts || 0,
        ...preview,
      };
    }
  );

export const offlineStatusSnapshot = async () => {
  const queue = await getOutbox();
  const conflicts = await getConflicts();
  return {
    pendingCount: _.filter(queue, (i) => i.status === "pending" || i.status === "failed").length,
    conflictCount: conflicts.length,
    pendingItems: slimOutboxForUi(queue),
  };
};

export const getConflicts = async () => {
  return (await forage.getItem(CONFLICTS_KEY)) || [];
};

export const addConflict = async (conflict) => {
  const list = await getConflicts();
  list.push(conflict);
  await forage.setItem(CONFLICTS_KEY, list);
  return conflict;
};

export const removeConflict = async (outboxId) => {
  const list = await getConflicts();
  await forage.setItem(
    CONFLICTS_KEY,
    _.reject(list, (c) => c.outboxId === outboxId)
  );
};

export const clearOutboxStore = () => forage.clear();

export const remapTempIdsInOutbox = async (clientTempId, serverId) => {
  if (!clientTempId || !serverId) return;
  const queue = await getOutbox();
  const next = _.map(queue, (item) => {
    let url = item.url;
    if (url && _.includes(url, String(clientTempId))) {
      url = url.replace(String(clientTempId), String(serverId));
    }
    const patch = { ...item, url };
    if (item.entityId === clientTempId || item.entityId === String(clientTempId)) {
      patch.entityId = serverId;
      patch.clientTempId = null;
    }
    return patch;
  });
  await setOutbox(next);
};
